import { useAuth } from '@/app/context';
import {
  useAdvisoryOptions,
  useBrokerOptions,
} from '@/features/reports/hooks/use-reports-filter-options.hook';
import { reportsService } from '@/services';
import { useDataTablePageSync } from '@/shared/hooks';
import {
  Button,
  DataTable,
  Menu,
  Pagination,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import {
  Download,
  FileSpreadsheet,
  Funnel,
  FunnelX,
  Printer,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { Receipt } from '../../../services/financial/types';
import { SearchFilterField } from '../../../shared/ui';
import { Page, PageHeader } from '../../../shared/ui/page';
import { toaster } from '../../../shared/ui/toaster.component';
import {
  f,
  formatCurrency,
  formatDateToBrazil,
  useFilters,
} from '../../../shared/utils';
import { formatRegisterNumber } from '../../../shared/utils/format-register-number';
import {
  mapReceiptApiRow,
  toReceiptsApiParams,
  useReceiptPdf,
  useReceipts,
  useReceiptsSummary,
} from '../hooks';
import { downloadReceiptDetailsCsv } from '../utils/receipt-details-export';
import { downloadReceiptsListCsv } from '../utils/receipts-list-export';
import { FinancialSummaryCards } from './financial-summary-cards.component';
import { ReceiptsFilters } from './receipts-filters.component';
import {
  createReceiptsColumns,
  type ReceiptAction,
} from './receipts-table-columns';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];
const DEFAULT_SORT_FIELD = 'receiptNumber';
const DEFAULT_SORT_TYPE = 'desc';

const RECEIPT_SORT_FIELD_BY_COLUMN_ID: Record<string, string> = {
  receiptNumber: 'receiptNumber',
  netValue: 'netValue',
};

const receiptStatusCardConfig = {
  paid: { label: 'Pagos', colorScheme: 'success' as const },
  pending: { label: 'Pendente', colorScheme: 'warning' as const },
};

/** Janela máxima do Período/Meses (regra de negócio). */
const MAX_PERIOD_MONTHS = 12;

/** Data de hoje em ISO yyyy-MM-dd (local). */
function todayIso(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
}

/** Subtrai `n` meses de uma data ISO yyyy-MM-dd (clamp no último dia do mês). */
function subtractMonthsIso(iso: string, n: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const base = new Date(year, month - 1 - n, 1);
  const lastDay = new Date(
    base.getFullYear(),
    base.getMonth() + 1,
    0
  ).getDate();
  const result = new Date(
    base.getFullYear(),
    base.getMonth(),
    Math.min(day, lastDay)
  );
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const dd = String(result.getDate()).padStart(2, '0');
  return `${result.getFullYear()}-${mm}-${dd}`;
}

/** Nº de meses (bruto) entre duas datas ISO yyyy-MM-dd. */
function monthsBetweenDates(from: string, to: string): number {
  const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
  const [toYear, toMonth, toDay] = to.split('-').map(Number);
  let months = (toYear - fromYear) * 12 + (toMonth - fromMonth);
  if (toDay < fromDay) months -= 1;
  return months;
}

export function MyReceiptsTab() {
  const { groups } = useAuth();
  const isInsurer = (groups ?? []).some(group => group.startsWith('insurer.'));
  const isAdvisory = (groups ?? []).some(group =>
    group.startsWith('advisory.')
  );
  // Corretor só vê a própria comissão (sem Valor Total e Comissão Assessoria).
  // Mesma regra do backend: grupo de corretor e não Seguradora.
  const isBroker =
    !isInsurer && (groups ?? []).some(group => group.startsWith('broker.'));

  // Opções reais: Assessoria só p/ Seguradora; Corretor p/ Seguradora + Assessoria.
  const { data: advisoryOptions = [] } = useAdvisoryOptions(isInsurer);
  const { data: brokerOptions = [] } = useBrokerOptions(
    isInsurer || isAdvisory
  );

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Período pré-preenchido com o último mês por padrão: [hoje - 1 mês, hoje].
  const today = todayIso();
  const defaultPeriodStart = subtractMonthsIso(today, 1);

  const { filters, debouncedFilters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(1000),
    broker: f.string().withDefault(''),
    advisory: f.string().withDefault(''),
    beneficiaryType: f.string().withDefault(''),
    searchType: f.string().withDefault(''),
    status: f.string().withDefault(''),
    dateFrom: f.string().withDefault(defaultPeriodStart),
    dateTo: f.string().withDefault(today),
    months: f.integer().withDefault(1),
    pageNo: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(10),
    sortField: f.string().withDefault(DEFAULT_SORT_FIELD),
    sortType: f.string().withDefault(DEFAULT_SORT_TYPE),
  });
  const dataTableRef = useDataTablePageSync<Receipt>(filters.pageNo);

  const receiptsParams = {
    page: debouncedFilters.pageNo,
    pageSize: debouncedFilters.pageSize,
    search: debouncedFilters.search,
    broker: debouncedFilters.broker || undefined,
    advisory: debouncedFilters.advisory || undefined,
    beneficiaryType: debouncedFilters.beneficiaryType || undefined,
    searchType: debouncedFilters.searchType || undefined,
    status: (debouncedFilters.status as Receipt['status'] | '') || undefined,
    dateFrom: debouncedFilters.dateFrom || undefined,
    dateTo: debouncedFilters.dateTo || undefined,
    months: debouncedFilters.months || undefined,
    sortField: debouncedFilters.sortField,
    sortType: (debouncedFilters.sortType === 'asc' ? 'asc' : 'desc') as
      | 'asc'
      | 'desc',
  };

  const { data, isLoading } = useReceipts(receiptsParams);
  const { data: summary, isLoading: isSummaryLoading } =
    useReceiptsSummary(receiptsParams);

  const receipts = data?.data ?? [];
  const totalPages = data?.meta.totalPages ?? 0;
  const totalItems = data?.meta.total ?? 0;

  const receiptPdf = useReceiptPdf();

  /** Recibo em PDF (2ª via): baixa o PDF real do i4pro pelo id do Lançamento. */
  const handleReceiptPdf = (receipt: Receipt) => {
    if (!receipt.receiptId) {
      toaster.create({
        title: 'Recibo em PDF',
        description: 'Recibo sem lançamento para gerar o PDF.',
        type: 'error',
      });
      return;
    }
    receiptPdf.mutate(
      { receiptId: receipt.receiptId, receiptNumber: receipt.receiptNumber },
      {
        onError: error =>
          toaster.create({
            title: 'Recibo em PDF',
            description:
              error instanceof Error
                ? error.message
                : 'Não foi possível gerar o PDF do recibo.',
            type: 'error',
          }),
      }
    );
  };

  const handleAction = (action: ReceiptAction, receipt: Receipt) => {
    if (action === 'pdf') {
      handleReceiptPdf(receipt);
      return;
    }
    downloadReceiptDetailsCsv(receipt);
  };

  /**
   * Exportar: consulta a API com os filtros atuais (todas as páginas) e baixa a
   * lista de recibos em CSV/Excel — independente da paginação da tela.
   */
  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const baseParams = toReceiptsApiParams(receiptsParams);
      const EXPORT_PAGE_SIZE = 200;
      const all: Receipt[] = [];
      let page = 1;
      for (;;) {
        const response = await reportsService.listReceipts({
          ...baseParams,
          page,
          pageSize: EXPORT_PAGE_SIZE,
        });
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Falha ao exportar');
        }
        all.push(...response.data.data.map(mapReceiptApiRow));
        const { total } = response.data.meta;
        if (all.length >= total || !response.data.data.length) break;
        page += 1;
      }

      if (!all.length) {
        toaster.create({
          title: 'Exportar',
          description: 'Nenhum recibo encontrado para os filtros atuais.',
          type: 'info',
        });
        return;
      }

      downloadReceiptsListCsv(all, { includeBeneficiary: isInsurer });
    } catch (error) {
      toaster.create({
        title: 'Exportar',
        description:
          error instanceof Error ? error.message : 'Não foi possível exportar.',
        type: 'error',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => createReceiptsColumns(isInsurer, handleAction),
    [isInsurer]
  );

  const sorting = useMemo(() => {
    if (!filters.sortField) return [];
    return [{ id: filters.sortField, desc: filters.sortType === 'desc' }];
  }, [filters.sortField, filters.sortType]);

  const clearFilters = () =>
    setFilters({
      ...filters,
      broker: '',
      advisory: '',
      beneficiaryType: '',
      searchType: '',
      status: '',
      dateFrom: defaultPeriodStart,
      dateTo: today,
      months: 1,
      pageNo: 1,
    });

  return (
    <div className='flex flex-col gap-4 sm:gap-6'>
      <FinancialSummaryCards
        summary={summary ?? null}
        isLoading={isSummaryLoading}
        visibleCards={isBroker ? ['brokerCommission'] : undefined}
      />

      <PageHeader.Filters>
        <div className='flex flex-col'>
          <div className='flex flex-col gap-3 md:flex-row md:items-center md:gap-4'>
            <SearchFilterField
              {...registerFilter('search')}
              placeholder='Buscar por recibo, valor, status...'
              className='w-full md:max-w-md'
            />
            <button
              type='button'
              onClick={() => setFiltersOpen(open => !open)}
              aria-expanded={filtersOpen}
              className='flex h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-alba-gray-300 bg-white px-4 text-sm font-semibold text-alba-gray-700 transition-colors hover:bg-alba-gray-50'
            >
              {filtersOpen ? (
                <FunnelX className='h-4 w-4' strokeWidth={1.8} />
              ) : (
                <Funnel className='h-4 w-4' strokeWidth={1.8} />
              )}
              Filtros
            </button>
            <Button
              variant='primary'
              size='md'
              className='md:ml-auto'
              loading={isExporting}
              onClick={() => void handleExport()}
            >
              <Download className='h-4 w-4' strokeWidth={2} />
              Exportar
            </Button>
          </div>

          <ReceiptsFilters
            open={filtersOpen}
            isInsurer={isInsurer}
            showBroker={isInsurer || isAdvisory}
            advisoryOptions={advisoryOptions}
            brokerOptions={brokerOptions}
            values={{
              broker: filters.broker,
              advisory: filters.advisory,
              beneficiaryType: filters.beneficiaryType,
              searchType: filters.searchType,
              status: filters.status,
              dateFrom: filters.dateFrom,
              dateTo: filters.dateTo,
              months: filters.months,
            }}
            onChange={patch => {
              const next = { ...filters, ...patch };
              if (patch.months !== undefined) {
                // Meses → Período (últimos N meses até hoje).
                next.dateTo = today;
                next.dateFrom = subtractMonthsIso(today, next.months);
              } else if (
                patch.dateFrom !== undefined ||
                patch.dateTo !== undefined
              ) {
                if (!next.dateFrom || !next.dateTo) {
                  // Período limpo → volta ao default (último mês).
                  next.dateTo = today;
                  next.dateFrom = subtractMonthsIso(today, 1);
                  next.months = 1;
                } else {
                  const span = monthsBetweenDates(next.dateFrom, next.dateTo);
                  if (span > MAX_PERIOD_MONTHS) {
                    toaster.create({
                      title: 'Período',
                      description: `O período não pode ultrapassar ${MAX_PERIOD_MONTHS} meses.`,
                      type: 'error',
                    });
                    return;
                  }
                  next.months = Math.max(1, span);
                }
              }
              setFilters({ ...next, pageNo: 1 });
            }}
            onClear={clearFilters}
          />
        </div>
      </PageHeader.Filters>

      <Page.Content className='min-w-0'>
        <div className='space-y-4 md:hidden'>
          {isLoading
            ? Array.from({ length: Math.min(filters.pageSize, 3) }).map(
                (_, index) => (
                  <div
                    key={index}
                    className='h-40 animate-pulse rounded-3xl bg-alba-gray-200'
                  />
                )
              )
            : receipts.map(receipt => {
                const config = receiptStatusCardConfig[receipt.status];
                return (
                  <article
                    key={receipt.id}
                    className='rounded-3xl bg-white p-4 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div>
                        <p className='text-xs text-alba-gray-600'>Recibo</p>
                        <p className='font-gotham font-bold text-alba-black-500'>
                          {receipt.receiptNumber}
                        </p>
                      </div>
                      <Menu.Root>
                        <Menu.Trigger />
                        <Menu.Content>
                          <Menu.Item
                            value='pdf'
                            icon={<Printer size={16} />}
                            onSelect={() => handleAction('pdf', receipt)}
                          >
                            Recibo em PDF
                          </Menu.Item>
                          <Menu.Item
                            value='excel'
                            icon={<FileSpreadsheet size={16} />}
                            onSelect={() => handleAction('excel', receipt)}
                          >
                            Detalhes em Excel
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Root>
                    </div>

                    <div className='mt-4 space-y-2'>
                      {isInsurer && (
                        <div className='flex items-start justify-between gap-3'>
                          <p className='text-sm font-semibold text-alba-black-500'>
                            Favorecido
                          </p>
                          <div className='text-right'>
                            <p className='text-sm text-alba-gray-700'>
                              {receipt.favorecidoName || '—'}
                            </p>
                            {receipt.favorecidoDocument && (
                              <p className='text-xs text-alba-gray-600'>
                                {formatRegisterNumber(
                                  receipt.favorecidoDocument
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Valor Líquido
                        </p>
                        <p className='text-sm text-alba-gray-700 tabular-nums'>
                          {formatCurrency(receipt.netValue)}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Previsão
                        </p>
                        <p className='text-sm text-alba-gray-700'>
                          {receipt.paymentForecast
                            ? formatDateToBrazil(receipt.paymentForecast)
                            : '—'}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Pagamento
                        </p>
                        <p className='text-sm text-alba-gray-700'>
                          {receipt.paymentDate
                            ? formatDateToBrazil(receipt.paymentDate)
                            : '—'}
                        </p>
                      </div>
                      <div className='flex items-center justify-between gap-3'>
                        <p className='text-sm font-semibold text-alba-black-500'>
                          Status
                        </p>
                        <Tag.Root
                          variant='outline'
                          colorScheme={config.colorScheme}
                          size='sm'
                        >
                          <Tag.Label>{config.label}</Tag.Label>
                        </Tag.Root>
                      </div>
                    </div>
                  </article>
                );
              })}

          {!isLoading && !receipts.length && (
            <div className='rounded-3xl bg-white p-6 text-center text-alba-gray-600 shadow-[0px_4px_24px_rgba(15,22,30,0.08)]'>
              Nenhum recibo encontrado.
            </div>
          )}

          {totalPages > 0 && (
            <div className='flex justify-center'>
              <Pagination
                count={totalPages * filters.pageSize}
                page={filters.pageNo}
                pageSize={filters.pageSize}
                totalItems={totalItems}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageChange={page =>
                  setFilters({
                    ...filters,
                    pageNo: page.page,
                    pageSize: page.pageSize,
                  })
                }
                onPageSizeSelect={pageSize =>
                  setFilters({ ...filters, pageNo: 1, pageSize })
                }
              />
            </div>
          )}
        </div>

        <div className='hidden min-w-0 overflow-hidden rounded-3xl md:block'>
          <DataTable
            ref={dataTableRef}
            className='min-w-0'
            loading={isLoading}
            columns={columns}
            data={receipts}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={filters.pageSize}
            manualSorting
            sorting={sorting}
            onSortChange={nextSorting => {
              const nextSort = nextSorting[0];
              const sortField = nextSort
                ? RECEIPT_SORT_FIELD_BY_COLUMN_ID[nextSort.id]
                : DEFAULT_SORT_FIELD;

              setFilters({
                ...filters,
                pageNo: 1,
                sortField: sortField ?? DEFAULT_SORT_FIELD,
                sortType: nextSort?.desc ? 'desc' : 'asc',
              });
            }}
            onPageChange={(page, pageSize) =>
              setFilters({ ...filters, pageNo: page, pageSize })
            }
          />
        </div>
      </Page.Content>
    </div>
  );
}
